import { execFile } from 'node:child_process';
import { readdir, stat } from 'node:fs/promises';
import { extname, join } from 'node:path';
import { promisify } from 'node:util';
import { DEFAULT_IGNORE_DIRS } from './ignore-patterns.js';
import { TEXT_EXTENSIONS } from './text-extensions.js';

const execFileAsync = promisify(execFile);

const TIMEOUT_MS = 5_000;
const IGNORE_DIRS: ReadonlySet<string> = new Set(DEFAULT_IGNORE_DIRS);

/**
 * Quickly estimate the number of indexable text files in a store path.
 *
 * Uses `git ls-files` for git repos (fast), falls back to a directory walk.
 * Filters by TEXT_EXTENSIONS. Returns 0 on timeout or error (never blocks store creation).
 */
export async function estimateFileCount(storePath: string): Promise<number> {
  try {
    return await withTimeout(estimateFileCountInner(storePath), TIMEOUT_MS);
  } catch {
    return 0;
  }
}

async function estimateFileCountInner(storePath: string): Promise<number> {
  // Try git ls-files first (fast for repos)
  const gitFiles = await gitLsFiles(storePath);
  if (gitFiles !== null) {
    return countTextFiles(gitFiles);
  }

  // Fall back to quick directory walk
  return walkAndCount(storePath);
}

async function gitLsFiles(repoPath: string): Promise<string[] | null> {
  try {
    // Check if .git exists
    await stat(join(repoPath, '.git'));

    // Clear GIT_DIR/GIT_WORK_TREE so git queries the repo at repoPath,
    // not a parent repo (e.g. when called inside a pre-commit hook)
    const cleanEnv = { ...process.env };
    delete cleanEnv['GIT_DIR'];
    delete cleanEnv['GIT_WORK_TREE'];
    delete cleanEnv['GIT_INDEX_FILE'];
    const { stdout } = await execFileAsync('git', ['ls-files', '-z'], {
      cwd: repoPath,
      maxBuffer: 50 * 1024 * 1024,
      timeout: TIMEOUT_MS,
      env: cleanEnv,
    });

    return stdout.split('\0').filter(Boolean);
  } catch {
    return null;
  }
}

function countTextFiles(files: string[]): number {
  let count = 0;
  for (const file of files) {
    const ext = extname(file).toLowerCase();
    if (TEXT_EXTENSIONS.has(ext)) {
      count++;
    }
  }
  return count;
}

async function walkAndCount(dirPath: string, depth = 0): Promise<number> {
  if (depth > 10) return 0;

  let count = 0;
  let entries;
  try {
    entries = await readdir(dirPath, { withFileTypes: true });
  } catch {
    return 0;
  }

  for (const entry of entries) {
    if (entry.isDirectory()) {
      if (IGNORE_DIRS.has(entry.name)) continue;
      count += await walkAndCount(join(dirPath, entry.name), depth + 1);
    } else if (entry.isFile()) {
      const ext = extname(entry.name).toLowerCase();
      if (TEXT_EXTENSIONS.has(ext)) {
        count++;
      }
    }
  }

  return count;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Timeout'));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    );
  });
}
